import { Link } from "react-router-dom";
import { Sheet } from "@/shared/ui/Sheet";
import { useAuth } from "@/shared/state/auth-context";
import { useTabHistory } from "@/shared/state/use-tab-history";
import { ACCENTS } from "@/shared/theme";
import { type Mode } from "@/shared/mode/types";
import { cn } from "@/shared/utils/cn";
import { Settings, LogIn, LogOut, Info, Film, PlaySquare, Home as HomeIcon } from "lucide-react";

type ModeCard = { mode: Mode; label: string; subtitle: string; icon: React.ReactNode };

const MODE_CARDS: ModeCard[] = [
  { mode: "instaflix",  label: "Instaflix",  subtitle: "Photos · Stories · Profiles", icon: <HomeIcon className="w-5 h-5" /> },
  { mode: "reelsflix",  label: "Reelsflix",  subtitle: "Vertical reels",              icon: <PlaySquare className="w-5 h-5" /> },
  { mode: "movieflix",  label: "Movieflix",  subtitle: "Movies & series",             icon: <Film className="w-5 h-5" /> },
];

export function ModeDrawer({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { currentMode, tapMode } = useTabHistory();
  const { currentUser, signOut } = useAuth();
  const close = () => onOpenChange(false);

  return (
    <Sheet open={open} onOpenChange={onOpenChange} side="left" widthClass="w-[80vw]">
      <div data-testid="mode-drawer" className="h-full flex flex-col p-6 pt-10">
        <span className="font-serif italic text-2xl text-gradient-rose tracking-widest">PikPok</span>
        <p className="text-white/40 text-xs mt-1 tracking-wider uppercase">Pick your world</p>

        <div className="mt-8 space-y-3">
          {MODE_CARDS.map((m) => {
            const active = currentMode === m.mode;
            return (
              <button
                key={m.mode}
                onClick={() => { tapMode(m.mode); close(); }}
                className={cn(
                  "block w-full text-left rounded-2xl p-4 border transition-all active:scale-[0.98]",
                  active ? "border-white/20" : "border-white/[0.06] hover:border-white/15",
                )}
                style={active ? { background: `linear-gradient(135deg, ${ACCENTS[m.mode].bgTint}, transparent)`, boxShadow: `0 0 30px ${ACCENTS[m.mode].glow}` } : undefined}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl glass flex items-center justify-center" style={{ color: ACCENTS[m.mode].primary }}>
                    {m.icon}
                  </div>
                  <div className="flex-1">
                    <div className="font-serif italic text-base text-white">{m.label}</div>
                    <div className="text-[11px] text-white/40 tracking-wider">{m.subtitle}</div>
                  </div>
                  {active && <span className="w-2 h-2 rounded-full" style={{ background: ACCENTS[m.mode].primary }} />}
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-auto space-y-1 text-sm text-white/70">
          <Link to="/settings" onClick={close} className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/5 active:scale-[0.98] transition-all">
            <Settings className="w-4 h-4" /> Settings
          </Link>
          {currentUser ? (
            <button onClick={() => { signOut(); close(); }} className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/5 active:scale-[0.98] transition-all">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          ) : (
            <Link to="/login" onClick={close} className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/5 active:scale-[0.98] transition-all">
              <LogIn className="w-4 h-4" /> Sign in
            </Link>
          )}
          <div className="flex items-center gap-3 px-3 py-3 rounded-xl text-white/40 text-xs">
            <Info className="w-4 h-4" /> PikPok · v8.6
          </div>
        </div>
      </div>
    </Sheet>
  );
}
